import { VerificationLogo } from '@seek/certsy-ui';
import { CertsyDialog } from '@seek/profile-base-ui';
import { useTranslations } from '@vocab/react';
import { useCallback, useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsVerificationsSeekPassDialogCallToAction } from 'src/modules/analytics/types';

import type { VerificationsSeekPassDialogAnalyticsOrigin } from '../analyticsUtils';

import translations from './.vocab';

interface Props {
  showDialog: boolean;
  closeDialogFn: () => void;
  url: string;
  origin: VerificationsSeekPassDialogAnalyticsOrigin;
  onSeekPassDialogClick: (arg: {
    callToAction: AnalyticsVerificationsSeekPassDialogCallToAction;
  }) => void;
  dialogTitle?: string;
  trackingEntityName?: string;
}

export const VerifyLicenceDialog = ({
  url,
  showDialog,
  closeDialogFn,
  dialogTitle = '',
  origin,
  onSeekPassDialogClick,
  trackingEntityName,
}: Props) => {
  const { t } = useTranslations(translations);

  const { seekPassDialogDisplayed } = useAnalytics();

  const onDialogDisplay = useCallback(() => {
    seekPassDialogDisplayed({
      origin,
      certsyType: 'licence',
      entityName: trackingEntityName,
    });
  }, [origin, seekPassDialogDisplayed, trackingEntityName]);

  const onClose = () => {
    closeDialogFn();
  };

  const onCertsyClick = () => {
    onSeekPassDialogClick({
      callToAction: 'verify on certsy',
    });
  };

  const onMaybeLaterClick = () => {
    onSeekPassDialogClick({ callToAction: 'maybe later' });
  };

  useEffect(() => {
    if (showDialog) {
      onDialogDisplay();
    }
  }, [onDialogDisplay, showDialog]);

  return (
    <CertsyDialog
      title={dialogTitle}
      bodyText={t('Help your application stand out...')}
      certsyButtonText={t('Verify on SEEK Pass')}
      seekPassLogo={<VerificationLogo />}
      certsyUrl={url}
      width="small"
      open={showDialog}
      onCertsyClick={onCertsyClick}
      onMaybeLaterClick={onMaybeLaterClick}
      onClose={onClose}
    />
  );
};
